import { isArrayEmpty } from 'helpers/ArrayHelpers';

import type {
  IContentfulAlphaLandingPageRaw,
  IContentfulAlphaLandingPageResponse,
  IContentfulBetaLandingPageRaw,
  IContentfulBetaLandingPageResponse,
  IContentfulLandingPageResponse
} from 'types';

import {
  ContentfulAlphaTemplateBlocks,
  ContentfulBetaTemplateBlocks,
  ContentfulLandingPageType,
  ContentfulSharedTemplateBlocks
} from 'types';

export async function toContentfulLandingPage(
  type: ContentfulLandingPageType,
  data: IContentfulLandingPageResponse
) {

  switch (type) {

    case ContentfulLandingPageType.Alpha: {
      const pageData = (data as IContentfulAlphaLandingPageResponse).alphaTemplatePageCollection.items[0];
      const page = await toAlphaLandingPage(pageData);
      return page;
    }

    case ContentfulLandingPageType.Beta: {
      const pageData = (data as IContentfulBetaLandingPageResponse).betaTemplatePageCollection.items[0];
      const page = await toBetaLandingPage(pageData);
      return page;
    }

    case ContentfulLandingPageType.Unknown: {
      const {
        alphaTemplatePageCollection: { items: alphaItems } = {},
        betaTemplatePageCollection: { items: betaItems } = {},
      } = data as (IContentfulAlphaLandingPageResponse & IContentfulBetaLandingPageResponse);

      if (!isArrayEmpty(alphaItems, true)) {
        const pageData = alphaItems[0];
        const page = await toAlphaLandingPage(pageData);
        return page;
      }

      if (!isArrayEmpty(betaItems, true)) {
        const pageData = betaItems[0];
        const page = await toBetaLandingPage(pageData);
        return page;
      }

      return false;
    }
    default:
      console.error('Invalid landing page type', type);
  }
}

async function toAlphaLandingPage(data: IContentfulAlphaLandingPageRaw) {

  const {
    sys: { firstPublishedAt: createdAt, publishedAt: updatedAt },
    slug,
    displayTitle,
    seoTitle,
    seoDescription,
    hasDirectLink,
    blocksCollection
  } = data || {};

  const blocks = await Promise.all(blocksCollection.items.map(async (block) => {
    const { __typename: blockType } = block;

    switch (blockType) {

      // Shared Template Blocks

      case ContentfulSharedTemplateBlocks.BannerBlock: {
        const { image, responsiveImage, altText } = block;
        return {
          image,
          responsiveImage,
          altText,
          blockType
        };
      }

      case ContentfulSharedTemplateBlocks.MediaBlock: {
        const {
          image, video, title, description, mediaPosition, hasGrayBackground,
        } = block;
        return {
          image,
          video,
          title,
          description,
          mediaPosition,
          hasGrayBackground,
          blockType
        };
      }

      case ContentfulSharedTemplateBlocks.ProductsBlock: {
        const {
          customListingSlug, isSingleProduct, blockTitle, isTitleHidden, listingType
        } = block;
        return {
          listingSlug: customListingSlug,
          listingType: listingType ? listingType === 'Promotion listing' ? 'promotion' : 'customListing' : 'customListing',
          title: blockTitle,
          isTitleHidden,
          isSingleProduct,
          blockType
        };
      }

      case ContentfulSharedTemplateBlocks.TextBlock: {
        const {
          title, description, titleAlign, descriptionAlign, type
        } = block;
        return {
          title,
          description,
          titleAlign,
          descriptionAlign,
          type,
          blockType
        };
      }

      // Alpha Template Blocks

      case ContentfulAlphaTemplateBlocks.BoxesBlock: {
        const {
          hasGrayBackground, hasPatternBackground, itemsCollection
        } = block;
        return {
          hasGrayBackground,
          hasPatternBackground,
          boxes: itemsCollection?.items?.map((item: any) => {
            const {
              title, description, image, disclaimers, type, buttonText, buttonType, buttonLink, buttonAnalyticsId,
            } = item || {};
            return {
              title,
              description,
              image,
              disclaimers,
              type,
              button: buttonText
                ? {
                  text: buttonText,
                  type: buttonType,
                  link: buttonLink || '',
                  analyticsId: buttonAnalyticsId || null
                } : null
            };
          }) || [],
          blockType
        };
      }

      case ContentfulAlphaTemplateBlocks.HighlightBlock: {
        const {
          text, textSize, type, image, isImageOutside
        } = block;
        return {
          text,
          textSize,
          type,
          image,
          blockType,
          isImageOutside
        };
      }

      default:
        console.error('Unknown landing page block type:', blockType);
    }
    return {};
  }));

  return {
    createdAt,
    updatedAt,
    slug,
    title: displayTitle,
    seoTitle,
    seoDescription,
    hasDirectLink,
    blocks,
    __typename: ContentfulLandingPageType.Alpha
  };
}

async function toBetaLandingPage(data: IContentfulBetaLandingPageRaw) {

  const {
    sys: { firstPublishedAt: createdAt, publishedAt: updatedAt },
    slug,
    displayTitle,
    seoTitle,
    seoDescription,
    hasDirectLink,
    blocksCollection,
    footerButtonLink, footerButtonText, footerDisclaimer, footerTitle, footerRegulation
  } = data || {};

  const blocks = await Promise.all(blocksCollection.items.map(async (block) => {
    const { __typename: blockType } = block;

    switch (blockType) {

      // Shared Template Blocks
      case ContentfulSharedTemplateBlocks.BannerBlock: {
        const { image, responsiveImage, altText } = block;
        return {
          image,
          responsiveImage,
          altText,
          blockType
        };
      }

      case ContentfulSharedTemplateBlocks.TextBlock: {
        const {
          title, description, titleAlign, descriptionAlign, type
        } = block;
        return {
          title,
          description,
          titleAlign,
          descriptionAlign,
          type,
          blockType
        };
      }

      // Beta Template Blocks
      case ContentfulBetaTemplateBlocks.DefaultBlock: {
        const {
          image, title, description, size,
        } = block;
        return {
          image,
          title,
          description,
          size,
          blockType,
        };
      }

      default:
        console.error('Unknown landing page block type:', blockType);
    }
    return {};
  }));

  return {
    createdAt,
    updatedAt,
    slug,
    title: displayTitle,
    seoTitle,
    seoDescription,
    hasDirectLink,
    blocks,
    footer: {
      title: footerTitle || null,
      button: {
        link: footerButtonLink || '',
        text: footerButtonText || '',
      },
      disclaimer: footerDisclaimer || null,
      regulation: footerRegulation || null
    },
    __typename: ContentfulLandingPageType.Beta
  };
}
